import type { Bundle, Resource, SubscriptionStatus } from "@medplum/fhirtypes";
import type { SubscriptionEvent, SubscriptionEventType } from "./types";

/**
 * Parse a subscription notification bundle into a typed event.
 *
 * Bundle structure from Medplum:
 * - entry[0].resource: SubscriptionStatus (event metadata)
 * - entry[1].resource: The actual FHIR resource (may be minimal for delete)
 */
export function parseSubscriptionBundle<T extends Resource>(bundle: Bundle): SubscriptionEvent<T> | null {
  const entries = bundle.entry ?? [];
  if (entries.length < 1) return null;

  const subscriptionStatus = entries[0]?.resource as SubscriptionStatus | undefined;
  if (!subscriptionStatus) return null;

  // Skip non-event notifications (handshake, heartbeat, etc.)
  if (subscriptionStatus.type !== "event-notification") return null;

  // Get resource from entry[1] if present
  const resource = entries[1]?.resource as T | undefined;

  // Extract event type - may indicate delete even without full resource
  const eventType = extractEventType(subscriptionStatus, resource ?? null);
  if (!eventType) return null;

  // For delete events, try to get resourceId from notificationEvent.focus
  if (eventType === "delete") {
    const resourceId = extractResourceIdFromStatus(subscriptionStatus) ?? resource?.id;
    if (!resourceId) return null;
    return {
      type: eventType,
      resource: resource ?? ({ id: resourceId } as T),
      resourceId,
    };
  }

  // For create/update, we need the full resource
  if (!resource || !resource.id) return null;

  return {
    type: eventType,
    resource,
    resourceId: resource.id,
  };
}

/**
 * Extract resource ID from SubscriptionStatus notificationEvent.focus
 */
function extractResourceIdFromStatus(status: SubscriptionStatus): string | null {
  const notificationEvents = status.notificationEvent ?? [];
  for (const event of notificationEvents) {
    // focus is a reference like "Patient/123"
    const focus = event.focus?.reference;
    if (focus) {
      const parts = focus.split("/");
      if (parts.length >= 2) {
        return parts[parts.length - 1];
      }
    }
  }
  return null;
}

/**
 * Check if SubscriptionStatus has a notificationEvent with focus reference.
 */
function hasNotificationFocus(status: SubscriptionStatus): boolean {
  const notificationEvents = status.notificationEvent ?? [];
  return notificationEvents.some((event) => event.focus?.reference);
}

/**
 * Check if resource appears to be a minimal/stub resource (delete notification).
 * Delete notifications may include minimal resource with just id/resourceType.
 */
function isMinimalResource<T extends Resource>(resource: T): boolean {
  // If no meta and only basic fields (id, resourceType), treat as minimal
  if (!resource.meta) {
    const keys = Object.keys(resource);
    return keys.length <= 2 && keys.every((k) => k === "id" || k === "resourceType");
  }
  return false;
}

/**
 * Extract event type from SubscriptionStatus using heuristics.
 *
 * Medplum doesn't include operation type in notifications, so we detect:
 * - Delete: no full resource but has focus reference, or minimal stub resource
 * - Upsert: full resource present (handler decides insert vs update based on local state)
 *
 * We return "update" for upsert since the handler checks local state to decide.
 */
function extractEventType<T extends Resource>(
  status: SubscriptionStatus,
  resource: T | null,
): SubscriptionEventType | null {
  // Delete: no resource but has focus reference
  if (!resource && hasNotificationFocus(status)) {
    return "delete";
  }

  // Delete: minimal stub resource (id/resourceType only)
  if (resource && isMinimalResource(resource)) {
    return "delete";
  }

  // Upsert: full resource present - return "update", handler checks local state
  return resource ? "update" : null;
}
